import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Users, Key, ChevronLeft, ChevronRight, Home, Package, Scale, List, MessageCircle, BookOpen, Shield, FileText } from 'lucide-react';


const AdminSidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();

  const menuItems = [
    { icon: Users, label: 'User Management', path: '/admin-user-management-dashboard' },
    { icon: Scale, label: 'Dispute Mediation', path: '/admin-dispute-mediation-dashboard' },
    { icon: Key, label: 'API Settings', path: '/api-settings' },
    { icon: Package, label: 'Komponente', path: '/component-showcase-documentation' },
  ];

  const moderationItems = [
    { icon: List, label: 'Moderacija Oglasa', path: '/admin-listings-moderation-dashboard' },
    { icon: MessageCircle, label: 'Moderacija Pregovora', path: '/admin-negotiations-moderation-dashboard' },
    { icon: BookOpen, label: 'Moderacija Diskusija', path: '/admin-discussions-moderation-dashboard' },
  ];

  const contentItems = [
    { icon: FileText, label: 'Sadržaj platforme', path: '/admin-content-editor' },
  ];

  const isActive = (path) => {
    return location?.pathname === path;
  };

  return (
    <div 
      className={`fixed left-0 top-0 h-full bg-gradient-to-b from-blue-600 to-blue-900 text-white transition-all duration-300 shadow-xl z-40 flex flex-col ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Header */}
      <div className="p-6 border-b border-blue-600 flex-shrink-0">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div>
              <h2 className="text-xl font-bold">Admin Panel</h2>
              <p className="text-blue-200 text-xs mt-1">Kontrolna tabla</p>
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-blue-700 transition-colors ml-auto"
            aria-label={isCollapsed ? 'Proširi' : 'Skupi'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation Menu - Scrollable */}
      <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
        {menuItems?.map((item) => {
          const IconComponent = item?.icon;
          const active = isActive(item?.path);

          return (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center p-3 rounded-lg transition-all duration-200 group ${
                active
                  ? 'bg-blue-700 shadow-lg'
                  : 'hover:bg-blue-700/50'
              }`}
              title={isCollapsed ? item?.label : ''}
            >
              <IconComponent className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-blue-200'}`} />
              
              {!isCollapsed && (
                <div className="ml-3 flex-1">
                  <p className={`font-medium ${active ? 'text-white' : 'text-blue-50'}`}>
                    {item?.label}
                  </p>
                </div>
              )}

              {active && !isCollapsed && (
                <div className="w-1 h-8 bg-white rounded-full" />
              )}
            </Link>
          );
        })}

        {/* Moderacija sadržaja group */}
        {!isCollapsed && (
          <div className="pt-3 pb-1">
            <div className="flex items-center gap-2 px-3 mb-2">
              <Shield className="w-3.5 h-3.5 text-blue-300" />
              <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Moderacija sadržaja</p>
            </div>
          </div>
        )}
        {isCollapsed && <div className="border-t border-blue-600/50 my-2" />}

        {moderationItems?.map((item) => {
          const IconComponent = item?.icon;
          const active = isActive(item?.path);

          return (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center p-3 rounded-lg transition-all duration-200 group ${
                active
                  ? 'bg-blue-700 shadow-lg'
                  : 'hover:bg-blue-700/50'
              }`}
              title={isCollapsed ? item?.label : ''}
            >
              <IconComponent className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-blue-200'}`} />
              
              {!isCollapsed && (
                <div className="ml-3 flex-1">
                  <p className={`font-medium text-sm ${active ? 'text-white' : 'text-blue-50'}`}>
                    {item?.label}
                  </p>
                </div>
              )}

              {active && !isCollapsed && (
                <div className="w-1 h-8 bg-white rounded-full" />
              )}
            </Link>
          );
        })}

        {/* Sadržaj platforme group */}
        {!isCollapsed && (
          <div className="pt-3 pb-1">
            <div className="flex items-center gap-2 px-3 mb-2">
              <FileText className="w-3.5 h-3.5 text-blue-300" />
              <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Sadržaj platforme</p>
            </div>
          </div>
        )}
        {isCollapsed && <div className="border-t border-blue-600/50 my-2" />}

        {contentItems?.map((item) => {
          const IconComponent = item?.icon;
          const active = isActive(item?.path);

          return (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex items-center p-3 rounded-lg transition-all duration-200 group ${
                active
                  ? 'bg-blue-700 shadow-lg'
                  : 'hover:bg-blue-700/50'
              }`}
              title={isCollapsed ? item?.label : ''}
            >
              <IconComponent className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-blue-200'}`} />
              
              {!isCollapsed && (
                <div className="ml-3 flex-1">
                  <p className={`font-medium text-sm ${active ? 'text-white' : 'text-blue-50'}`}>
                    {item?.label}
                  </p>
                </div>
              )}

              {active && !isCollapsed && (
                <div className="w-1 h-8 bg-white rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Back to Home */}
      <div className="p-4 border-t border-blue-600 flex-shrink-0">
        <Link
          to="/"
          className="flex items-center p-3 rounded-lg hover:bg-blue-700 transition-colors"
          title={isCollapsed ? 'Početna' : ''}
        >
          <Home className="w-5 h-5 text-blue-200" />
          {!isCollapsed && (
            <span className="ml-3 text-blue-50">Nazad na početnu</span>
          )}
        </Link>
      </div>
    </div>
  );
};

export default AdminSidebar;